import { Row, Col } from 'ui/blocks/Grid'
import AlertBar from 'ui/components/AlertBar'
import { Field } from 'shared/form'
import Skeleton from 'ui/blocks/Skeleton'
import * as addressHelper from '../helpers/addressHelper'

import isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'
import { useFormikContext } from 'formik'
import { useTranslation } from 'shared/utils/useTranslation'
import React, { useEffect, useState } from 'react'

/**
 * A pure component to render and validate an address.
 *
 * @param {array} countries - A list of countries.
 * @param {array} provinces - A list of provinces.
 * @param {boolean} showFirstAndLastName - Whether to show the first and last name fields.
 * @param {string} addressIdentifier - A suffix for the address fields name.
 * @param {string} defaultCountryId - A fallback country id if the country is not found in the countries list.
 * @param {string} defaultFormLayout - A fallback form layout if the country is invalid. Find an example below
 * with all supported fields.
 * @param {boolean} validateOnChangeOnly - Whether to validate the fields only when they change.
 * @param {boolean} skipValidation - Whether to skip the validation.
 * @param {string} invalidExistingAddressWarning - A warning message to display when an existing address is invalid.
 * @param {boolean} showInvalidExistingAddressWarning - Whether to show the invalid existing address warning.
 * @param {number} addressPropertyIndex - The index of the address property in the properties array.
 * In the customer form for example, it's the index of the address in the customer properties array.
 * @param {object} initialValues - The initial values for the address fields.
 *
 * @example
 *  <AddressFields
 *    countries={countries}
 *    provinces={provinces}
 *    showFirstAndLastName
 *    addressIdentifier="billing_address"
 *    defaultCountryId="2"
 *    defaultFormLayout="{first_name}{last_name}\n{address1}\n{address2}\n{zipcode}{city}{province}\n{country}"
 *    validateOnChangeOnly={false}
 *    skipValidation={false}
 *    invalidExistingAddressWarning="The saved address seems to be invalid, please review the information."
 *    initialValues={{
 *      first_name: 'John',
 *      last_name: 'Doe',
 *      country: 'US',
 *      address1: '123 Main St',
 *      address2: 'Apt 1',
 *      city: 'New York',
 *      region: 'NY',
 *      zipcode: '10001',
 *      country_id: null,
 *      province_id: null
 *    }}
 *  />
 */

const AddressFields = (props) => {
  const {
    autoFocus = false,
    disabled,
    showFirstAndLastName = false,
    defaultCountryId,
    countries,
    provinces,
    addressIdentifier,
    errorKey,
    fieldTranslationPrefix,
    selectPlaceholder,
    invalidExistingAddressWarning,
    showInvalidExistingAddressWarning = true,
    validateOnChangeOnly = false,
    skipValidation = false,
    defaultFormLayout,
    addressPropertyIndex,
    fieldDebounce = 0,
    initialValues
  } = props

  const { t } = useTranslation('user')
  const { values, setFieldValue, errors } = useFormikContext()

  const [countrySelectOptions, setCountrySelectOptions] = useState([])
  const [provinceSelectOptions, setProvinceSelectOptions] = useState([])
  const [fieldNamesArray, setFieldNamesArray] = useState([])
  const [selectedCountry, setSelectedCountry] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  const FORM_LAYOUT_FALLBACK = '{first_name}{last_name}\n{country}\n{address1}\n{address2}\n{zipcode}{city}{province}'

  // Hooks

  useEffect(() => {
    if (isEmpty(countries) || isEmpty(provinces)) return

    setIsLoading(false)
  }, [countries, provinces])

  useEffect(() => {
    if (isEmpty(selectedCountry)) return

    const countryProvinceOptions = buildProvinceSelectOptions(selectedCountry.id)
    setProvinceSelectOptions(countryProvinceOptions)
  }, [selectedCountry, provinces])

  useEffect(() => {
    const countrySelectOptions = buildCountrySelectOptions()
    setCountrySelectOptions(countrySelectOptions)
  }, [countries])

  useEffect(() => {
    // Build the field names array based on the selected country
    const formLayout = selectedCountry?.form_layout || defaultFormLayout || FORM_LAYOUT_FALLBACK
    const fieldNames = addressHelper.buildFieldNamesArray(formLayout, showFirstAndLastName)
    setFieldNamesArray(fieldNames)
  }, [selectedCountry, defaultFormLayout])

  useEffect(() => {
    let existingCountry = findCountry(valueForFieldWithIdentifier('country_id'))

    // Fall back to the default country if both the country_id and the country are not present
    if (isEmpty(existingCountry) && isEmpty(valueForFieldWithIdentifier('country')) && defaultCountryId) {
      existingCountry = findCountry(defaultCountryId)
      setValueToFieldWithIdentifier('country_id', existingCountry?.id)
    }

    setSelectedCountry(existingCountry)
  }, [countrySelectOptions, defaultCountryId, initialValues])

  useEffect(() => {
    handleErrors()
  }, [errors])

  // Event handlers

  const handleCountryChange = (countryId) => {
    if (countryId === selectedCountry?.id) return

    const newSelectedCountry = findCountry(countryId)
    cleanUpNotVisibleFieldValuesForLayout(selectedCountry, newSelectedCountry)

    setSelectedCountry(newSelectedCountry)
    setValueToFieldWithIdentifier('country', newSelectedCountry.name)

    // At this point, the province list will be updated with the provinces of the new country.
    // Therefore, it only resets the region value if the province_id (from the previous country) is present.
    // If the province_id is not present, the region can be a string like 'New York' from an old address
    // before the new component, and it should not be reset.
    if (!isEmpty(valueForFieldWithIdentifier('province_id'))) {
      setValueToFieldWithIdentifier('region', '')
    }

    setValueToFieldWithIdentifier('province_id', '')
    setValueToFieldWithIdentifier('country_id', countryId)
  }

  const handleProvinceChange = (provinceId) => {
    setValueToFieldWithIdentifier('region', provinceId)
    setValueToFieldWithIdentifier('province_id', provinceId)
  }

  // Helpers

  const setValueToFieldWithIdentifier = (fieldName, value) => {
    setFieldValue(buildFieldNameWithIdentifier(fieldName), value)
  }

  const valueForFieldWithIdentifier = (fieldName) => {
    const addressValues = addressHelper.extractValuesForIdentifier(values, addressIdentifier)

    return !isEmpty(addressValues) ? addressValues[fieldName] : ''
  }

  const findCountry = (countryId) => {
    return countries?.find((country) => country.id === countryId)
  }

  const findProvince = (provinceId, countryId) => {
    return provinces?.find((province) => province.country_id === countryId && province.id === provinceId)
  }

  // Add the address identifier to the field name if it's present
  const buildFieldNameWithIdentifier = (fieldName) => {
    return isEmpty(addressIdentifier) ? fieldName : `${addressIdentifier}.${fieldName}`
  }

  const buildCountrySelectOptions = () => {
    return countries?.map((country) => ({ value: country.id, label: country.name }))
  }

  const buildProvinceSelectOptions = (countryId) => {
    return provinces?.filter((province) => province.country_id === countryId)
      .map((province) => ({ value: province.id, label: province.name }))
  }

  const buildField = (fieldName) => {
    const validate = (value) => validateField(fieldName, value)
    const onInputHandler = (e) => handleTextFieldChange(fieldName, e)
    const label = addressHelper.buildFieldLabel({
      name: fieldName,
      country: selectedCountry,
      translationPrefix: fieldTranslationPrefix
    })
    const placeholder = addressHelper.buildFieldPlaceholder({
      name: fieldName,
      country: selectedCountry,
      translationPrefix: fieldTranslationPrefix
    })
    const fieldNameWithIdentifier = buildFieldNameWithIdentifier(fieldName)
    const showPlaceholder = !disabled

    switch (fieldName) {
      case 'first_name':
      case 'last_name':
      case 'address1':
      case 'address2':
      case 'city':
        return (
          <Field
            disabled={disabled}
            name={fieldNameWithIdentifier}
            label={t(label)}
            validate={validate}
            placeholder={showPlaceholder && t(placeholder)}
            onInput={onInputHandler}
            debounce={fieldDebounce}
            autoFocus={['first_name', 'address1'].includes(fieldName) && autoFocus}
            fast
          />
        )
      case 'zipcode':
        return (
          <Field
            disabled={disabled}
            name={fieldNameWithIdentifier}
            label={t(label)}
            validate={validate}
            placeholder={(showPlaceholder && selectedCountry?.zipcode_placeholder) ?? t(placeholder)}
            onInput={onInputHandler}
            debounce={fieldDebounce}
            fast
          />
        )
      case 'country':
        return (
          <Field
            disabled={disabled}
            name={fieldNameWithIdentifier}
            label={t(label)}
            type="select"
            placeholder={showPlaceholder && selectPlaceholder}
            options={countrySelectOptions}
            onChange={handleCountryChange}
            validate={validate}
            value={selectedCountryOption()}
            // forceValue ensures it uses the value above instead of the value from the formik context
            forceValue
            data-tid="Country"
            usePortal
          />
        )
      case 'province': {
        // Use 'region' as the field name to make it backwards compatible with the API
        const regionFieldName = buildFieldNameWithIdentifier('region')

        if (provinceSelectOptions?.length > 0) {
          return (
            <Field
              disabled={disabled}
              name={regionFieldName}
              label={t(label)}
              type="select"
              placeholder={showPlaceholder && selectPlaceholder}
              options={provinceSelectOptions}
              onChange={handleProvinceChange}
              validate={validate}
              value={selectedProvinceOption()}
              forceValue
              data-tid="Province"
              usePortal
            />
          )
        } else {
          // Render a text field if the country does not have provinces
          return (
            <Field
              disabled={disabled}
              name={regionFieldName}
              label={t(label)}
              validate={validate}
              placeholder={showPlaceholder && t(placeholder)}
              debounce={fieldDebounce}
              fast
            />
          )
        }
      }
    }
  }

  const selectedCountryOption = () => {
    const selectedOption = countrySelectOptions?.find(
      (option) => option.value === selectedCountry?.id
    )

    if (!isEmpty(selectedOption)) return selectedOption

    const country = valueForFieldWithIdentifier('country')

    return !isEmpty(country) ? { label: country, value: country } : null
  }

  const selectedProvinceOption = () => {
    const selectedOption = provinceSelectOptions?.find(
      (option) => option.value === valueForFieldWithIdentifier('province_id')
    )

    if (!isEmpty(selectedOption)) return selectedOption

    const region = valueForFieldWithIdentifier('region')

    return !isEmpty(region) ? { label: region, value: region } : null
  }

  const handleTextFieldChange = (fieldName, e) => {
    if (fieldName !== 'zipcode') return

    e.target.value = e.target.value.toUpperCase()
  }

  // Return a validation error message for the field if it's invalid
  const validateField = (fieldName, value) => {
    if (skipValidation) return

    if (['first_name', 'last_name', 'address2'].includes(fieldName)) return

    if (validateOnChangeOnly && !isDirty) return

    if (fieldName === 'country' && findCountry(selectedCountry?.id)) return

    if (fieldName === 'address1' && !isEmpty(value?.trim())) return

    if (fieldName === 'city' && !isEmpty(value?.trim())) return

    // When the province is selected for the first time,
    // the value (which is the region field value) has the province id.
    // When the address is loaded from the backend, the province id is already present in the form.
    if (fieldName === 'province' && validateProvince(value, valueForFieldWithIdentifier('province_id'))) return

    if (fieldName === 'zipcode' && validateZipcode(value)) return

    return addressHelper.buildFieldError({
      name: fieldName,
      country: selectedCountry,
      t,
      errorKey,
      translationPrefix: fieldTranslationPrefix
    })
  }

  const validateProvince = (province, provinceId) => {
    const countryHasNoProvinces = provinceSelectOptions?.length === 0 || false
    const provinceNotRequired = selectedCountry?.province_required !== true

    const existingProvince = findProvince(province, selectedCountry?.id) ||
      findProvince(provinceId, selectedCountry?.id)

    return !selectedCountry || provinceNotRequired || countryHasNoProvinces || existingProvince
  }

  const validateZipcode = (value) => {
    const zipcodeNotRequired = selectedCountry?.zipcode_required !== true
    const noFormatRequired = isEmpty(selectedCountry?.zipcode_format)
    const hasValue = !isEmpty(value?.trim())

    const regex = `^${selectedCountry?.zipcode_format}$`
    const matchFormat = value?.match(regex)

    return !selectedCountry || zipcodeNotRequired || (noFormatRequired && hasValue) || matchFormat
  }

  const cleanUpNotVisibleFieldValuesForLayout = (oldCountry, newCountry) => {
    const fieldsToCleanUp = notVisibleFieldsForLayout(newCountry.form_layout)

    if (oldCountry) {
      if (oldCountry.city_autofill) {
        fieldsToCleanUp.push('city')
      }
      if (oldCountry.zipcode_autofill) {
        fieldsToCleanUp.push('zipcode')
      }
    }

    cleanUpFields(fieldsToCleanUp)
  }

  const notVisibleFieldsForLayout = (formLayout) => {
    const fieldNames = addressHelper.buildFieldNamesArray(formLayout, showFirstAndLastName).flat()
    const visibleFields = fieldNames.map((field) => field === 'province' ? 'region' : field)
    // These fields are the ones that can be hidden by the form layout
    const dynamicFields = ['first_name', 'last_name', 'city', 'region', 'zipcode']

    return dynamicFields.filter((fieldName) => !visibleFields.includes(fieldName))
  }

  const cleanUpFields = (fields) => {
    fields.forEach((field) => {
      // Using empty string instead of `null` to avoid the warning "`value` prop on `input` should not be null.
      // Consider using an empty string to clear the component or `undefined` for uncontrolled components".
      setValueToFieldWithIdentifier(field, '')
    })
  }

  const handleErrors = () => {
    const errorMessages = []
    const addressErrorsKey = 'base'

    // Example:
    // errors = {
    //   properties_attributes: [
    //     { base: ['The address is invalid'] },
    //     {},
    //     {}
    //   ]
    // }
    if (errors) {
      Object.keys(errors).forEach((key) => {
        // Check if it can have multiple addresses (ex: customer addresses)
        if (addressIdentifier?.includes(key)) {
          // ex: addressIdentifier = "properties_attributes[1]"
          const error = errors[key] && errors[key][addressPropertyIndex]
          const addressErrors = error && error[addressErrorsKey]

          if (addressErrors && addressErrors.length > 0) {
            errorMessages.push(addressErrors[0])
          }
        } else {
          // It's a single address (ex: location address)
          // Example:
          // errors = {
          //   main_address.base: ['The address is invalid']
          // }
          const error = errors[key]
          const isAddressError = error && error.length > 0 && key.includes(addressErrorsKey)

          if (isAddressError) {
            errorMessages.push(error[0])
          }
        }
      })
    }

    setErrorMessage(errorMessages.join('. '))
  }

  // It replaces the "dirty" prop from Formik. It smartly compares the current values with the initial values
  // ignoring missing fields and null values.
  const isDirty = addressHelper.addressDirty(values, initialValues, addressIdentifier, defaultCountryId)

  // Only display the warning for existing invalid addresses
  const showInvalidAddressWarning = showInvalidExistingAddressWarning && valueForFieldWithIdentifier('meets_validation_requirements') === false

  // Render
  const renderFields = () => (
    <Row>
      {fieldNamesArray.map((fields) => {
        return fields.map((field) => (
          <Col md={`${12 / fields.length}`} key={field}>
            {buildField(field)}
          </Col>
        ))
      })}
    </Row>
  )

  if (isLoading) {
    return <Skeleton width="1000" />
  } else {
    return (
      <>
        {showInvalidAddressWarning && <AlertBar type="warning">{invalidExistingAddressWarning}</AlertBar>}
        {errorMessage && <AlertBar type="warning">{errorMessage}</AlertBar>}
        {renderFields()}
      </>
    )
  }
}

AddressFields.propTypes = {
  disabled: PropTypes.bool,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      alpha2: PropTypes.string.isRequired,
      form_layout: PropTypes.string.isRequired,
      zipcode_required: PropTypes.bool.isRequired,
      zipcode_format: PropTypes.string,
      zipcode_type: PropTypes.string,
      zipcode_placeholder: PropTypes.any,
      province_required: PropTypes.bool.isRequired,
      province_type: PropTypes.string
    })
  ).isRequired,
  provinces: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      country_id: PropTypes.string.isRequired
    })
  ).isRequired,
  showFirstAndLastName: PropTypes.bool,
  addressIdentifier: PropTypes.string,
  defaultCountryId: PropTypes.string,
  defaultFormLayout: PropTypes.string,
  errorKey: PropTypes.string,
  fieldTranslationPrefix: PropTypes.string,
  selectPlaceholder: PropTypes.string,
  validateOnChangeOnly: PropTypes.bool,
  skipValidation: PropTypes.bool,
  invalidExistingAddressWarning: PropTypes.any,
  showInvalidExistingAddressWarning: PropTypes.bool,
  addressPropertyIndex: PropTypes.number,
  fieldDebounce: PropTypes.number,
  autoFocus: PropTypes.bool,
  initialValues: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    country: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    region: PropTypes.string,
    zipcode: PropTypes.string,
    country_id: PropTypes.string,
    province_id: PropTypes.string,
    meets_validation_requirements: PropTypes.bool
  })
}

export default AddressFields
